// Client landing — built to help a stockist place their next order: their offer progress, shop by
// category, what sells best ONLINE in their market, one-click restock of their own best sellers,
// their reorder rhythm, what's new — plus what they owe and their orders & invoices.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StatusBadge from '../components/StatusBadge.jsx';
import { listOrdersPage, invoiceUrl } from '../api/orders.js';
import Pager from '../components/Pager.jsx';
import WhatYouOwe from '../components/WhatYouOwe.jsx';
import PromotionsBanner from '../components/PromotionsBanner.jsx';
import { paymentStatusLabel } from '../lib/labels.js';
import { myStatementUrl } from '../api/companies.js';
import { fetchMyHome, fetchMyPromotions, fetchClientHomePreview, fetchClientPromotions } from '../api/me.js';
import { useAuthStore } from '../stores/authStore.js';
import { fmtMoney, fmtOrderTotal } from '../lib/matrix.js';
import { promoProgress } from '../lib/promotions.js';
import { safeGet } from '../lib/storage.js';

const orderRef = (o) => o.orderNumber || o.shopifyOrderName || o.id.slice(-8);
const productUrl = (p) => `/catalog?product=${encodeURIComponent(String(p.productId).split('/').pop())}`;
const marginPct = (p) => (p.price != null && p.rrp ? Math.round((1 - p.price / p.rrp) * 100) : null);
const weeksAgo = (d) => {
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 864e5);
  if (days < 7) return days <= 1 ? 'this week' : `${days} days ago`;
  const w = Math.round(days / 7);
  return w === 1 ? '1 week ago' : `${w} weeks ago`;
};

/** The client's order in progress (saved in this browser by the order screen) → its goods subtotal. */
function draftSubtotal(home, role) {
  const keys = [`wholesale.orderDraft.${role}.self`];
  if (home?.store && home?.companyLocationId) keys.unshift(`wholesale.orderDraft.${role}.${home.store}:${home.companyLocationId}`);
  for (const k of keys) {
    try {
      const d = JSON.parse(safeGet(k) || 'null');
      if (d?.lines?.length) {
        let sum = 0;
        let units = 0;
        for (const l of d.lines) for (const s of Object.values(l.sizes || {})) {
          sum += (Number(s.qty) || 0) * (Number(s.price) || 0);
          units += Number(s.qty) || 0;
        }
        if (units) return { subtotal: Math.round(sum * 100) / 100, units };
      }
    } catch {
      /* a corrupt draft — ignore */
    }
  }
  return null;
}

function timeLeft(endsAt, now) {
  const ms = new Date(endsAt).getTime() - now;
  if (ms <= 0) return null;
  const d = Math.floor(ms / 864e5);
  const h = Math.floor((ms % 864e5) / 36e5);
  const m = Math.floor((ms % 36e5) / 6e4);
  return d ? `${d}d ${String(h).padStart(2, '0')}h` : `${h}h ${String(m).padStart(2, '0')}m`;
}

function ProductImage({ src, alt, className = '' }) {
  return src ? (
    <img src={src} alt={alt} loading="lazy" className={`h-full w-full object-cover ${className}`} />
  ) : (
    <div aria-hidden="true" className={`h-full w-full bg-surface-raised ${className}`} />
  );
}

function Price({ p, currency }) {
  if (p.price == null) return null;
  const m = marginPct(p);
  return (
    <>
      <div className="text-[13px] text-text-secondary">
        <strong className="text-text-primary">{p.from ? 'From ' : ''}{fmtMoney(p.price, p.currency || currency)}</strong> wholesale
        {p.rrp ? <span className="text-text-muted"> · RRP {fmtMoney(p.rrp, p.currency || currency)}</span> : null}
      </div>
      {m != null && m > 0 && <div className="text-xs font-semibold text-success">{m}% margin</div>}
    </>
  );
}

// --- offer progress ------------------------------------------------------------------------------
function OfferHero({ promos, draft }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60e3);
    return () => clearInterval(t);
  }, []);
  const live = (promos?.promotions || []).filter((p) => p.status === 'LIVE');
  const cur = promos?.currency;
  const subtotal = draft?.subtotal || 0;
  const prog = promoProgress(live, subtotal);
  if (!live.length) return null;
  const p = live[0];
  const tiers = p.type === 'TIERED' ? p.tiers : [];
  const left = p.endsAt ? timeLeft(p.endsAt, now) : null;
  const top = tiers.length ? tiers[tiers.length - 1].minSubtotal : 0;
  const pct = top ? Math.min(100, Math.round((subtotal / top) * 100)) : 0;
  const headline = prog.next
    ? `Add ${fmtMoney(prog.next.gap, cur)} to your order to unlock ${prog.next.percent}% off`
    : prog.current
      ? `Your order gets ${prog.current.percent}% off — ${fmtMoney(prog.current.saving, cur)} saved`
      : p.type === 'TIERED'
        ? `Save up to ${Math.max(...tiers.map((t) => t.percent))}% on your next order`
        : `${p.percent}% off every order`;
  return (
    <section aria-label="Your offer" className="flex flex-wrap items-center gap-6 rounded-2xl bg-accent px-6 py-6 text-white sm:px-7">
      <div className="min-w-0 flex-[2_1_420px]">
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider text-orange-300">
          {p.name}
          {left && <span className="rounded-full bg-orange-700 px-2.5 py-0.5 normal-case tracking-normal text-white">Ends in {left}</span>}
        </div>
        <h2 className="mt-2 text-xl font-bold tracking-tight sm:text-2xl">{headline}</h2>
        <p className="mt-1.5 text-sm text-slate-300">
          {draft ? `Your order in progress is ${fmtMoney(subtotal, cur)}` : 'Start an order'} — applied automatically, no code needed.
        </p>
        {tiers.length > 0 && (
          <div className="mt-4">
            <div className="h-2.5 overflow-hidden rounded-full bg-slate-700" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Progress to the top tier">
              <div className="h-full rounded-full bg-orange-600" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-2 flex justify-between gap-2 text-xs text-slate-300">
              {tiers.map((t) => (
                <span key={t.minSubtotal}>
                  <strong className="text-white">{t.percent}%</strong> from {fmtMoney(t.minSubtotal, cur).replace(/\.00$/, '')}
                  {subtotal >= t.minSubtotal ? ' ✓' : ''}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-[1_1_200px] flex-col gap-2">
        <Link to="/catalog" className="rounded-lg bg-white px-4 py-3 text-center text-[15px] font-bold text-accent hover:bg-slate-100">
          {draft ? 'Continue your order' : 'Start an order'}
        </Link>
        <Link to="/promotions" className="py-1 text-center text-sm text-white underline">
          How the offer works
        </Link>
      </div>
    </section>
  );
}

// --- online best sellers carousel ------------------------------------------------------------
function OnlineBestSellers({ online, currency }) {
  const ref = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: false });
  const update = () => {
    const el = ref.current;
    if (!el) return;
    setEdge({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8 });
  };
  useEffect(update, [online]);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.9, behavior: 'smooth' });
  return (
    <section aria-label={online.title} className="rounded-2xl border border-line bg-surface p-5 shadow-sm sm:p-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-wider text-orange-700">
            What shoppers {online.scope === 'country' ? 'near you' : 'online'} are buying
          </div>
          <h2 className="mt-1 text-lg font-bold text-text-primary sm:text-xl">{online.title}</h2>
          <p className="mt-0.5 text-[13px] text-text-muted">Our top sellers online in the last {online.days} days — proven demand for your shelves.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" data-preview-ok onClick={() => scroll(-1)} disabled={edge.start} aria-label="Previous best sellers" className="flex h-10 w-10 items-center justify-center rounded-full border border-line-strong bg-surface hover:bg-surface-raised disabled:opacity-35">
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
          </button>
          <button type="button" data-preview-ok onClick={() => scroll(1)} disabled={edge.end} aria-label="More best sellers" className="flex h-10 w-10 items-center justify-center rounded-full border border-line-strong bg-surface hover:bg-surface-raised disabled:opacity-35">
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
          </button>
        </div>
      </div>
      <div ref={ref} onScroll={update} className="-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-1 pb-2 [scrollbar-width:thin]">
        {online.items.map((p) => (
          <article key={p.productId} className="flex w-[calc(50%-8px)] shrink-0 snap-start flex-col gap-1.5 sm:w-[calc(33.333%-11px)] lg:w-[calc(25%-12px)]">
            <Link to={productUrl(p)} className="relative block aspect-[4/5] overflow-hidden rounded-xl bg-surface-raised">
              <ProductImage src={p.image} alt={p.title} />
              <span className="absolute left-2.5 top-2.5 flex h-7 min-w-7 items-center justify-center rounded-full bg-white px-1.5 text-[13px] font-extrabold text-text-primary shadow-sm">#{p.rank}</span>
              <span className={`absolute inset-x-2 bottom-2 truncate rounded-full px-2 py-1 text-center text-[11px] font-bold shadow-sm ${p.stocked ? 'bg-white text-text-secondary' : 'bg-orange-700 text-white'}`}>
                {p.stocked ? 'In your store' : 'Not in your store'}
              </span>
            </Link>
            <Link to={productUrl(p)} className="mt-1 line-clamp-2 text-sm font-semibold text-text-primary hover:underline">{p.title}</Link>
            <Price p={p} currency={currency} />
            <Link to={productUrl(p)} className="mt-1 rounded-lg border border-line-strong py-2 text-center text-[13px] font-semibold text-text-primary hover:bg-surface-raised">
              {p.stocked ? 'Restock' : 'Add to order'}
            </Link>
          </article>
        ))}
      </div>
    </section>
  );
}

// --- restock -------------------------------------------------------------------------------------
function Restock({ items, currency }) {
  const navigate = useNavigate();
  const [skip, setSkip] = useState([]);
  const chosen = items.filter((p) => !skip.includes(p.productId));
  const total = chosen.reduce((s, p) => s + p.total, 0);
  const retail = chosen.every((p) => p.retail != null) ? chosen.reduce((s, p) => s + p.retail, 0) : null;
  const units = chosen.reduce((s, p) => s + p.qty, 0);
  const addAll = () =>
    navigate('/catalog', {
      state: {
        reorder: chosen.flatMap((p) => p.sizes.map((s) => ({ variantId: s.variantId, quantity: s.qty }))),
        merge: true,
        label: `${chosen.length} restock style${chosen.length === 1 ? '' : 's'}`,
      },
    });
  const stockPill = {
    in: ['In stock', 'bg-success-light text-success'],
    low: ['Low stock', 'bg-warning-light text-warning'],
    'some-out': ['Some sizes out', 'bg-warning-light text-warning'],
  };
  return (
    <section aria-label="Time to restock" className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
      <div className="px-5 pb-3 pt-5">
        <h2 className="text-lg font-bold text-text-primary">Time to restock your best sellers</h2>
        <p className="mt-0.5 text-[13px] text-text-muted">What you order most, with the sizes from your last order — capped to what’s in stock.</p>
      </div>
      <ul className="border-t border-line">
        {items.map((p) => {
          const off = skip.includes(p.productId);
          const [label, cls] = stockPill[p.stock] || stockPill.in;
          return (
            <li key={p.productId} className={`flex gap-3 border-b border-surface-raised px-4 py-3 sm:px-5 ${off ? 'opacity-45' : ''}`}>
              <Link to={productUrl(p)} className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-surface-raised">
                <ProductImage src={p.image} alt="" />
              </Link>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <Link to={productUrl(p)} className="line-clamp-2 text-sm font-semibold text-text-primary hover:underline">{p.title}</Link>
                  <div className="shrink-0 text-sm font-semibold tabular-nums text-text-primary">{fmtMoney(p.total, p.currency || currency)}</div>
                </div>
                <div className="mt-0.5 text-xs text-text-muted">
                  You ordered {p.lastQty} · {weeksAgo(p.lastOrderedAt)}
                </div>
                <div className="mt-0.5 text-xs text-text-secondary">{p.sizes.map((s) => `${s.size} ${s.qty}`).join(' · ')}</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>
                  <span className="ml-auto flex gap-1 whitespace-nowrap">
                    <Link to={productUrl(p)} className="rounded-md px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-raised hover:text-text-primary">Change sizes</Link>
                    <button
                      type="button"
                      onClick={() => setSkip((l) => (off ? l.filter((x) => x !== p.productId) : [...l, p.productId]))}
                      aria-label={off ? `Put ${p.title} back` : `Leave out ${p.title}`}
                      className="rounded-md px-2 py-1 text-xs font-medium text-text-muted hover:bg-surface-raised hover:text-text-primary"
                    >
                      {off ? 'Put back' : 'Remove'}
                    </button>
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center justify-between gap-3 bg-surface-raised/50 px-5 py-4">
        <div className="text-[13px] leading-relaxed text-text-secondary">
          <strong className="text-base text-text-primary">{fmtMoney(total, currency)}</strong> · {units} units across {chosen.length} style{chosen.length === 1 ? '' : 's'}
          {retail != null && retail > total && (
            <>
              <br />
              <span className="font-semibold text-success">
                Sells for {fmtMoney(retail, currency)} at RRP — about {fmtMoney(retail - total, currency)} margin for you
              </span>
            </>
          )}
        </div>
        <button type="button" onClick={addAll} disabled={!chosen.length} className="rounded-lg bg-accent px-5 py-2.5 text-sm font-bold text-white hover:bg-accent-hover disabled:opacity-40">
          Add {chosen.length === items.length ? 'all' : chosen.length} to my order
        </button>
      </div>
    </section>
  );
}

function ProductGrid({ items, currency, badge }) {
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {items.map((p) => (
        <article key={p.productId} className="flex flex-col overflow-hidden rounded-xl border border-line bg-surface">
          <Link to={productUrl(p)} className="relative block aspect-[4/5] bg-surface-raised">
            <ProductImage src={p.image} alt={p.title} />
            {badge && <span className="absolute left-2.5 top-2.5 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold tracking-wider text-text-primary shadow-sm">{badge}</span>}
          </Link>
          <div className="flex flex-1 flex-col gap-1 p-3">
            <Link to={productUrl(p)} className="line-clamp-2 text-sm font-semibold text-text-primary hover:underline">{p.title}</Link>
            <Price p={p} currency={currency} />
            <Link to={productUrl(p)} className="mt-auto rounded-lg border border-line-strong py-2 text-center text-[13px] font-semibold text-text-primary hover:bg-surface-raised">
              Add to order
            </Link>
          </div>
        </article>
      ))}
    </div>
  );
}

// --- page --------------------------------------------------------------------------------------
/**
 * preview: { companyId, layout? } — staff/reps see this client's page with the client's own data;
 * every button and link is disabled (only the carousel scrolls), and `layout` overrides the saved
 * section layout (Settings shows unsaved changes live).
 */
export default function ClientHome({ preview = null }) {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [home, setHome] = useState(null);
  const [promos, setPromos] = useState(null);
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const PAGE_SIZE = 25;

  const companyId = preview?.companyId || null;
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    setHome(null);
    (companyId ? fetchClientHomePreview(companyId) : fetchMyHome()).then(setHome).catch(() => setHome({ ready: false }));
    (companyId ? fetchClientPromotions(companyId) : fetchMyPromotions()).then(setPromos).catch(() => setPromos(null));
  }, [companyId]);
  useEffect(() => {
    setLoading(true);
    listOrdersPage({ page, pageSize: PAGE_SIZE, ...(companyId ? { companyId } : {}) })
      .then((r) => {
        setOrders(r.orders);
        setTotal(r.total);
        setSummary(r.summary || []);
      })
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [page, companyId]);

  // Lifetime figures come from the server across EVERY order (rejected / cancelled excluded).
  const totals = useMemo(() => {
    const byCur = {};
    let units = 0;
    let count = 0;
    for (const g of summary) {
      byCur[g.currency] = (byCur[g.currency] || 0) + Number(g.subtotal || 0);
      units += Number(g.units || 0);
      count += Number(g.orders || 0);
    }
    return { byCur, units, count };
  }, [summary]);

  // The order in progress lives in the client's own browser, so a preview can't see it.
  const draft = useMemo(() => (companyId ? null : draftSubtotal(home, user?.role || 'CLIENT')), [home, user?.role, companyId]);
  const layout = (preview?.layout || home?.layout?.sections || []).filter((x) => x.on).map((x) => x.key);
  const order = layout.length || home?.layout ? layout : ['offer', 'categories', 'online', 'restock', 'fresh'];
  // Preview: swallow every click except the carousel arrows, and say why.
  const guard = companyId
    ? (e) => {
        if (e.target.closest('[data-preview-ok]')) return;
        if (!e.target.closest('a,button,input,select,textarea,label')) return;
        e.preventDefault();
        e.stopPropagation();
        setBlocked(true);
        setTimeout(() => setBlocked(false), 2200);
      }
    : undefined;
  const firstName = home?.firstName || (user?.name || '').split(' ')[0];
  const cur = home?.currency;
  const r = home?.rhythm;
  const due = r?.avgDays && r.daysSince >= r.avgDays - 7;
  const hasLive = (promos?.promotions || []).some((p) => p.status === 'LIVE');
  const orderAgain = (o) =>
    navigate('/catalog', { state: { reorder: (o.lineItems || []).map((l) => ({ variantId: l.variantId, quantity: l.quantity })), merge: true, label: `Order ${orderRef(o)}` } });

  // The movable sections, rendered in the order staff chose (Settings → Client home).
  const blocks = {
    offer: (
      <>
      {hasLive ? <OfferHero promos={promos} draft={draft} /> : companyId ? null : <PromotionsBanner />}
      {!hasLive && draft && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-surface px-5 py-4 shadow-sm">
          <span className="text-sm text-text-secondary">You have an order in progress — <strong className="text-text-primary">{fmtMoney(draft.subtotal, cur)}</strong> · {draft.units} units</span>
          <Link to="/catalog" className="rounded-lg bg-accent px-4 py-2 text-sm font-bold text-white hover:bg-accent-hover">Continue your order</Link>
        </div>
      )}
      </>
    ),
    categories: (
      <>
      {/* Shop by category */}
      {home?.categories?.length > 0 && (
        <section aria-label="Shop by category">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-lg font-bold text-text-primary">Shop by category</h2>
            <Link to="/catalog" className="text-[13px] font-semibold text-text-primary hover:underline">Browse everything</Link>
          </div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {home.categories.map((c) => (
              <Link key={c.key} to={`/catalog?type=${encodeURIComponent(c.key)}`} className="group overflow-hidden rounded-2xl border border-line bg-surface">
                <div className="relative aspect-[4/3] bg-surface-raised">
                  <ProductImage src={c.image} alt="" className="transition duration-300 group-hover:scale-[1.03]" />
                  {c.newStyles > 0 && <span className="absolute bottom-2.5 left-2.5 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-text-primary shadow-sm">{c.newStyles} NEW</span>}
                </div>
                <div className="flex flex-col px-3.5 py-3 sm:flex-row sm:items-baseline sm:justify-between">
                  <span className="text-[15px] font-bold text-text-primary">{c.label}</span>
                  <span className="text-xs text-text-muted">{c.styles} styles</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
      </>
    ),
    online: (
      <>
      {home?.online && <OnlineBestSellers online={home.online} currency={cur} />}
      </>
    ),
    restock: (
      <>
      {/* Restock + rhythm / recent orders */}
      {(home?.restock?.length > 0 || due || orders.length > 0) && (
        <div className="flex flex-wrap items-start gap-6">
          {home?.restock?.length > 0 && (
            <div className="min-w-0 flex-[2_1_560px]">
              <Restock items={home.restock} currency={cur} />
            </div>
          )}
          <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-6">
            {r && (due || (!r.avgDays && r.daysSince >= 60)) && (
              <section aria-label="Reorder reminder" className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
                <div className="text-xs font-bold uppercase tracking-wider text-amber-800">Running low?</div>
                <p className="mt-1.5 text-[15px] leading-snug text-text-primary">
                  {r.avgDays ? (
                    <>
                      You usually reorder every <strong>{Math.max(1, Math.round(r.avgDays / 7))} weeks</strong>. Your last order was <strong>{weeksAgo(Date.now() - r.daysSince * 864e5)}</strong>.
                    </>
                  ) : (
                    <>
                      Your last order was <strong>{weeksAgo(Date.now() - r.daysSince * 864e5)}</strong>.
                    </>
                  )}
                </p>
              </section>
            )}
            {orders.length > 0 && (
              <section aria-label="Your last orders" className="rounded-2xl border border-line bg-surface px-5 py-4 shadow-sm">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-[15px] font-bold text-text-primary">Your last orders</h2>
                  <a href="#orders" className="text-xs font-semibold text-text-primary hover:underline">All orders</a>
                </div>
                <ul>
                  {orders.slice(0, 3).map((o) => (
                    <li key={o.id} className="flex items-center justify-between gap-2 border-b border-surface-raised py-2.5 text-[13px] last:border-0">
                      <Link to={`/orders/${o.id}`} className="min-w-0">
                        <div className="font-semibold text-text-primary">
                          {orderRef(o)} <span className="font-normal text-text-muted">· {new Date(o.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span>
                        </div>
                        <div className="mt-0.5 text-text-muted">{o.status === 'APPROVED' ? paymentStatusLabel(o.paymentStatus) : <StatusBadge status={o.status} />}</div>
                      </Link>
                      <div className="shrink-0 text-right">
                        <div className="font-semibold tabular-nums text-text-primary">{fmtOrderTotal(o)}</div>
                        {o.lineItems?.length > 0 && !['REJECTED', 'CANCELLED'].includes(o.status) && (
                          <button type="button" onClick={() => orderAgain(o)} className="text-xs font-medium text-text-primary underline hover:text-accent">
                            Order again
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      )}
      </>
    ),
    fresh: (
      <>
      {/* New since your last order */}
      {home?.fresh?.length > 0 && (
        <section aria-label="New products">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-lg font-bold text-text-primary">
              {home.freshMode === 'since-last-order' ? 'New since your last order' : home.freshMode === 'recent' ? 'New this season' : 'Latest arrivals'}
              {home.freshCount > home.fresh.length && <span className="ml-1 text-[13px] font-medium text-text-muted">· {home.freshCount} styles</span>}
            </h2>
            <Link to="/catalog" className="text-[13px] font-semibold text-text-primary hover:underline">See the catalogue</Link>
          </div>
          <ProductGrid items={home.fresh.slice(0, 4)} currency={cur} badge={home.freshMode === 'latest' ? null : 'NEW'} />
        </section>
      )}
      </>
    ),
  };

  return (
    <div onClickCapture={guard} className="relative mx-auto flex max-w-6xl flex-col gap-6">
      {blocked && (
        <div role="status" className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-md bg-gray-900 px-4 py-2 text-sm text-white shadow-lg">
          Preview only — buttons and links are disabled.
        </div>
      )}
      {/* Greeting + account manager */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary sm:text-[28px]">{firstName ? `Welcome back, ${firstName}` : 'Welcome back'}</h1>
          <p className="mt-1 text-sm text-text-muted">
            {home?.company ? `${home.company}${home.locationName && home.locationName !== home.company ? ` · ${home.locationName}` : ''} — ` : ''}here’s what to order next.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {home?.manager && (
            <div className="flex items-center gap-3 rounded-xl border border-line bg-surface px-3.5 py-2.5">
              <div aria-hidden="true" className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-raised text-sm font-bold text-text-secondary">
                {home.manager.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <div className="text-[13px] leading-tight">
                <div className="font-semibold text-text-primary">{home.manager.name}</div>
                <div className="text-text-muted">{home.manager.role}</div>
              </div>
              <a href={`mailto:${home.manager.email}`} className="ml-1 rounded-lg border border-line px-3 py-1.5 text-[13px] font-semibold text-text-primary hover:bg-surface-raised">Email</a>
            </div>
          )}
          <Link to="/catalog" className="rounded-lg bg-accent px-4 py-2.5 text-sm font-bold text-white hover:bg-accent-hover">+ Place an order</Link>
        </div>
      </div>

      <WhatYouOwe companyId={companyId} />

      {order.map((k) => (blocks[k] ? <div key={k} className="contents">{blocks[k]}</div> : null))}

      {/* Orders & invoices */}
      <section id="orders" aria-label="Your orders and invoices" className="scroll-mt-6">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-bold text-text-primary">Your orders &amp; invoices</h2>
          <a href={myStatementUrl()} target="_blank" rel="noreferrer" className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised">
            Download statement
          </a>
        </div>
        {!loading && orders.length > 0 && (
          <div className="mb-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-text-muted">Orders</div>
              <div className="mt-1 text-2xl font-semibold text-text-primary">{totals.count}</div>
            </div>
            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-text-muted">Total units</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums text-text-primary">{totals.units}</div>
            </div>
            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-text-muted" title="Goods ordered, before shipping (cancelled and declined orders excluded)">Total ordered</div>
              <div className="mt-1 space-y-0.5">
                {Object.entries(totals.byCur).map(([c, amt]) => (
                  <div key={c} className="text-lg font-semibold tabular-nums text-text-primary">{fmtMoney(amt, c)}</div>
                ))}
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-text-muted">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="rounded-lg border border-dashed border-line bg-surface p-12 text-center">
            <p className="text-sm font-medium text-text-primary">No orders yet</p>
            <p className="mt-1 text-sm text-text-muted">Your orders and invoices will appear here once you place your first order.</p>
            <Link to="/catalog" className="mt-4 inline-block rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover">
              Place your first order
            </Link>
          </div>
        ) : (
          <>
            {/* Phones: one card per order (tap to open), invoice download on the card. */}
            <ul className="divide-y divide-line rounded-lg border border-line bg-surface shadow-sm md:hidden">
              {orders.map((o) => (
                <li key={o.id} className="flex items-center gap-3 px-3 py-3">
                  <Link to={`/orders/${o.id}`} className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-text-primary">{orderRef(o)}</span>
                      <span className="text-sm font-semibold tabular-nums">{fmtOrderTotal(o)}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-text-muted">
                      <StatusBadge status={o.status} />
                      {o.status === 'APPROVED' && <span className="text-text-secondary">{paymentStatusLabel(o.paymentStatus)}</span>}
                      <span>· {new Date(o.createdAt).toLocaleDateString()}</span>
                    </div>
                  </Link>
                  <a href={invoiceUrl(o.id)} target="_blank" rel="noreferrer" className="shrink-0 rounded-md border border-line px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:border-accent hover:text-accent">
                    Invoice
                  </a>
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto rounded-lg border border-line bg-surface shadow-sm md:block">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-line bg-surface-raised/60 text-left text-xs uppercase tracking-wide text-text-muted">
                    <th className="px-4 py-2.5 font-medium">Order</th>
                    <th className="px-4 py-2.5 font-medium">Date</th>
                    <th className="px-4 py-2.5 font-medium">Status</th>
                    <th className="px-4 py-2.5 font-medium">Payment</th>
                    <th className="px-4 py-2.5 text-right font-medium">Units</th>
                    <th className="px-4 py-2.5 text-right font-medium">Total</th>
                    <th className="px-4 py-2.5 text-right font-medium">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-line transition last:border-0 hover:bg-surface-raised/40">
                      <td className="px-4 py-3">
                        <Link to={`/orders/${o.id}`} className="font-medium text-text-primary hover:text-accent hover:underline">
                          {orderRef(o)}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-text-muted">{new Date(o.createdAt).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={o.status} />
                      </td>
                      <td className="px-4 py-3 text-xs text-text-secondary">{o.status === 'APPROVED' ? paymentStatusLabel(o.paymentStatus) : '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{o.unitCount}</td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums">{fmtOrderTotal(o)}</td>
                      <td className="px-4 py-3 text-right">
                        <a href={invoiceUrl(o.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-line px-2.5 py-1 text-xs font-medium text-text-secondary hover:border-accent hover:text-accent">
                          Download PDF
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {!loading && <Pager page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} />}
      </section>
    </div>
  );
}
